import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Mic, LoaderCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAudioRecording } from "@/hooks/use-audio-recording";
import { toast } from "sonner";
import { AudioRecorder } from "@/features/chat/components/AudioRecorder";
import { AudioPreview } from "@/features/chat/components/MultimodalPreview";
import { uploadAudioToStorage } from "@/features/chat/services/audio-upload-service";
import { parseRelativeStoragePath } from "@/lib/storage-url";
import { Textarea } from "@/components/ui/textarea";
import type { StorageAudioBlock } from "@/features/chat/types/messages";
import { useStorageContext } from "@/contexts/use-storage";
import type { PendingAudioData } from "./voice-input-helpers";

export interface VoiceTextInputProps {
  // Core
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  maxLength?: number;

  // Layout
  layout: "vertical" | "horizontal";

  // Voice recording
  enableVoice?: boolean;
  onAudioUrlsChange?: (audioUrls: string[]) => void;
  /** Callback when upload process starts or ends */
  onActiveProcessChange?: (isActive: boolean) => void;
  maxRecordingDuration?: number; // default 60s
  /** Initial audio URLs (for editing existing descriptions) */
  initialAudioUrls?: string[];

  // Extra actions (file upload, submit, etc.)
  renderActions?: () => React.ReactNode;

  // Styling
  className?: string;
  textareaClassName?: string;

  // Textarea props
  autoFocus?: boolean;
  onKeyDown?: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  onPaste?: (e: React.ClipboardEvent<HTMLTextAreaElement>) => void;
}

/**
 * Reusable textarea with voice recording functionality.
 *
 * Supports two layouts:
 * - "horizontal": Textarea left, action buttons right (for ChatInput)
 * - "vertical": Textarea top, buttons in footer (for ImageDescriptionDialog)
 *
 * Features:
 * - Auto-growing textarea
 * - Voice recording with waveform visualization
 * - Audio upload to Firebase Storage
 * - Audio preview/playback
 */
export function VoiceTextInput({
  value,
  onChange,
  placeholder,
  disabled = false,
  maxLength,
  layout,
  enableVoice = true,
  onActiveProcessChange,
  onAudioUrlsChange,
  maxRecordingDuration = 60,
  initialAudioUrls = [],
  renderActions,
  className,
  textareaClassName,
  autoFocus = false,
  onKeyDown,
  onPaste,
}: VoiceTextInputProps) {
  const { userId, conversationId } = useStorageContext();
  const { t } = useTranslation("chat");
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Track if we've initialized from initial URLs
  const initializedRef = useRef(false);

  // Audio state - initialize empty, will be populated from initialAudioUrls on first render
  const [audioData, setAudioData] = useState<PendingAudioData[]>([]);

  // Initialize audio data from initialAudioUrls only once
  // Parse relative paths like "audio/filename.webm" to extract fileName
  useEffect(() => {
    if (!initializedRef.current && initialAudioUrls.length > 0) {
      initializedRef.current = true;
      setAudioData(
        initialAudioUrls.map((relativePath, idx) => {
          // Parse relative path to extract fileName
          const parsed = parseRelativeStoragePath(relativePath);
          return {
            id: `existing-${idx}`,
            url: relativePath, // Store relative path (will be converted to download URL when needed)
            fileName: parsed?.fileName,
            duration: 0,
          };
        })
      );
    }
  }, [initialAudioUrls]);

  // Computed: completed audio storage paths (for onAudioUrlsChange callback)
  // Return relative paths like "audio/{fileName}" instead of full URLs
  const completedAudioUrls = useMemo(
    () =>
      audioData
        .filter((d) => !d.uploading && d.fileName)
        .map((d) => `audio/${d.fileName!}`),
    [audioData]
  );

  // Track previous values to avoid unnecessary callbacks
  const prevAudioUrlsRef = useRef<string[]>([]);
  const prevActiveProcessRef = useRef<boolean>(false);

  // Notify parent when audio URLs change (only when actually different)
  useEffect(() => {
    const urlsChanged =
      completedAudioUrls.length !== prevAudioUrlsRef.current.length ||
      completedAudioUrls.some((url, i) => url !== prevAudioUrlsRef.current[i]);

    if (urlsChanged) {
      prevAudioUrlsRef.current = completedAudioUrls;
      onAudioUrlsChange?.(completedAudioUrls);
    }
  }, [completedAudioUrls, onAudioUrlsChange]);

  // Computed: check if any audio is currently uploading
  const hasActiveProcess = audioData.some((d) => d.uploading);

  // Notify parent when active process state changes (only when actually different)
  useEffect(() => {
    if (hasActiveProcess !== prevActiveProcessRef.current) {
      prevActiveProcessRef.current = hasActiveProcess;
      onActiveProcessChange?.(hasActiveProcess);
    }
  }, [hasActiveProcess, onActiveProcessChange]);

  // Audio recording hook
  const {
    state: recordingState,
    duration: recordingDuration,
    analyserNode,
    startRecording,
    stopRecording,
    cancelRecording,
    error: recordingError,
  } = useAudioRecording({ maxDuration: maxRecordingDuration });

  const isRecording = recordingState === "recording";
  const isStartingRecording = recordingState === "requesting";

  // Show error toast when recording fails
  useEffect(() => {
    if (recordingError) {
      toast.error(t("multimodal.imageDescription.recordingFailed", "Opname mislukt"), {
        description: recordingError,
      });
    }
  }, [recordingError, t]);

  // Remove a specific audio by index with blob URL cleanup
  const handleRemoveAudio = useCallback((index: number) => {
    setAudioData((prev) => {
      const audioToRemove = prev[index];
      // Cleanup blob URL if it exists
      if (audioToRemove?.url.startsWith("blob:")) {
        URL.revokeObjectURL(audioToRemove.url);
      }
      return prev.filter((_, i) => i !== index);
    });
  }, []);

  // Async upload function - runs in background after recording stops
  const uploadAudio = useCallback(
    async (recordingResult: { blob: Blob; duration: number }, tempId: string) => {
      if (!userId || !conversationId) {
        toast.error(t("multimodal.imageDescription.uploadFailed", "Audio upload niet beschikbaar"));
        setAudioData((prev) => prev.filter((d) => d.id !== tempId));
        return;
      }

      try {
        // Upload to Firebase with progress callback and duration metadata
        const uploadResult = await uploadAudioToStorage(
          recordingResult.blob,
          userId,
          conversationId,
          {
            duration: recordingResult.duration,
            onProgress: (progress) => {
              setAudioData((prev) =>
                prev.map((d) => (d.id === tempId ? { ...d, uploadProgress: progress } : d))
              );
            },
          }
        );

        // Mark as complete and store the fileName
        // Keep the blob URL for local preview - don't replace with Firebase URL
        setAudioData((prev) =>
          prev.map((d) =>
            d.id === tempId
              ? {
                  ...d,
                  fileName: uploadResult.fileName,
                  uploading: false,
                }
              : d
          )
        );
      } catch (error) {
        console.error("Audio upload error:", error);
        toast.error(t("multimodal.imageDescription.uploadFailed", "Audio upload mislukt"));
        // Keep the audio but mark as failed
        setAudioData((prev) =>
          prev.map((d) => (d.id === tempId ? { ...d, uploading: false, error: true } : d))
        );
      }
    },
    [userId, conversationId, t]
  );

  // Handle stop recording - immediately shows player, uploads async
  const handleStopRecording = useCallback(async () => {
    const result = await stopRecording();
    if (!result) return;

    // IMMEDIATE: Create blob URL and show player right away
    const blobUrl = URL.createObjectURL(result.blob);
    const tempId = `temp-${Date.now()}`;

    // Add pending audio block with blob URL for immediate preview
    setAudioData((prev) => [
      ...prev,
      {
        id: tempId,
        url: blobUrl,
        duration: result.duration,
        uploading: true,
        uploadProgress: 0,
      },
    ]);

    // ASYNC: Upload (and optionally transcribe) in background (non-blocking)
    uploadAudio(result, tempId);
  }, [stopRecording, uploadAudio]);

  // Handle start recording
  const handleStartRecording = useCallback(async () => {
    await startRecording();
  }, [startRecording]);

  // Render audio previews
  const renderAudioPreviews = () => {
    if (audioData.length === 0) return null;

    return (
      <div className="border-t border-utrecht-blue/20">
        {audioData.map((audio, index) => {
          const audioBlock: StorageAudioBlock = {
            type: "audio",
            source_type: "storage",
            mime_type: "audio/webm",
            relative_gcs_path: `audio/${audio.fileName || audio.id}`,
            metadata: {
              duration: audio.duration,
            },
            uploading: audio.uploading,
            uploadProgress: audio.uploadProgress,
          };
          return (
            <AudioPreview
              key={audio.id}
              block={audioBlock}
              removable={!isRecording && !audio.uploading}
              onRemove={() => handleRemoveAudio(index)}
              label={audioData.length > 1 ? index + 1 : undefined}
              variant="full"
            />
          );
        })}
      </div>
    );
  };

  // Render mic button
  const renderMicButton = () => {
    if (!enableVoice) return null;

    return (
      <button
        type="button"
        onClick={handleStartRecording}
        disabled={isRecording || isStartingRecording || disabled}
        className={cn(
          "size-9 inline-flex items-center justify-center transition-all",
          isRecording || isStartingRecording || disabled
            ? "bg-muted text-muted-foreground cursor-not-allowed"
            : "bg-utrecht-red hover:bg-utrecht-red text-white"
        )}
        aria-label={t("multimodal.imageDescription.startRecording", "Start opname")}
      >
        {isStartingRecording ? <LoaderCircle className="size-5 animate-spin" /> : <Mic className="size-5" />}
      </button>
    );
  };

  // HORIZONTAL LAYOUT (for ChatInput and ImageDescriptionDialog)
  if (layout === "horizontal") {
    return (
      <div className={cn("flex flex-col bg-utrecht-light-blue", className)}>
        {/* Audio previews - top */}
        {renderAudioPreviews()}

        {/* Audio recorder - shows when recording */}
        <AudioRecorder
          isRecording={isRecording}
          duration={recordingDuration}
          analyserNode={analyserNode}
          onStop={handleStopRecording}
          onCancel={cancelRecording}
          className="border-t border-utrecht-blue/20"
        />

        {/* Main input row - bottom */}
        <div className="relative flex border-t border-utrecht-blue/20 flex-1 min-h-0">
          {/* Textarea container - scrolls only when content exceeds available space */}
          <div className="flex-1 min-h-[108px]">
            <textarea
              ref={textareaRef}
              value={value}
              onChange={(e) => onChange(e.target.value)}
              onKeyDown={onKeyDown}
              onPaste={onPaste}
              placeholder={placeholder}
              maxLength={maxLength}
              rows={3}
              disabled={disabled || isRecording}
              autoFocus={autoFocus}
              className={cn(
                "w-full field-sizing-content resize-none border-none bg-transparent px-4 py-3 shadow-none ring-0 outline-none focus:ring-0 focus:outline-none min-h-full",
                textareaClassName
              )}
            />
          </div>

          {/* Vertical action bar on the right */}
          <div className="flex flex-col gap-1 p-2 border-l border-utrecht-blue/20">
            {renderMicButton()}
            {renderActions?.()}
          </div>
        </div>
      </div>
    );
  }

  // VERTICAL LAYOUT (for ImageDescriptionDialog)
  return (
    <div className={cn("flex flex-col", className)}>
      {/* Audio previews - top */}
      {renderAudioPreviews()}

      {/* Audio recorder - shows when recording */}
      <AudioRecorder
        isRecording={isRecording}
        duration={recordingDuration}
        analyserNode={analyserNode}
        onStop={handleStopRecording}
        onCancel={cancelRecording}
      />

      {/* Textarea - bottom */}
      <Textarea
        ref={textareaRef}
        id="voice-text-input"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={onKeyDown}
        onPaste={onPaste}
        placeholder={placeholder}
        maxLength={maxLength}
        disabled={disabled || isRecording}
        autoFocus={autoFocus}
        className={cn(
          "min-h-[110px] rounded-none resize-none transition-all",
          "focus:border-primary focus:ring-2 focus:ring-primary/20",
          textareaClassName
        )}
      />

      {/* Footer with mic button and actions */}
      <div className="flex items-center justify-between bg-muted/50 p-4 border-t border-border">
        {renderMicButton()}
        <div className="flex gap-3">{renderActions?.()}</div>
      </div>
    </div>
  );
}
